import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ChevronDown,
  ChevronUp,
  ImageIcon,
  Loader2,
  Plus,
  Save,
  SlidersHorizontal,
  Trash2,
  Upload,
} from 'lucide-react';
import toast from 'react-hot-toast';
import Sidebar from '../../components/Sidebar';
import { ApiError } from '../../lib/api';
import {
  createSlider,
  deleteSlider,
  getSlides,
  updateSlider,
} from '../../services/slider';
import type { Slider } from '../../types/slider';
import {
  PRODUCT_IMAGE_ACCEPT,
  PRODUCT_IMAGE_MAX_BYTES,
  validateProductImageFile,
} from '../../utils/productImageUpload';

interface SliderFormState {
  title: string;
  subtitle: string;
  link: string;
  sort_order: number;
  is_active: boolean;
  imageUrl: string | null;
  imageFile?: File;
}

function sliderToForm(slider: Slider): SliderFormState {
  return {
    title: slider.title,
    subtitle: slider.subtitle ?? '',
    link: slider.link ?? '',
    sort_order: slider.sort_order,
    is_active: slider.is_active,
    imageUrl: slider.image,
  };
}

function emptySliderForm(sortOrder: number): SliderFormState {
  return {
    title: '',
    subtitle: '',
    link: '',
    sort_order: sortOrder,
    is_active: true,
    imageUrl: null,
  };
}

function Field({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  placeholder?: string;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-medium text-slate-700">{label}</span>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-900 shadow-sm transition focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-100"
      />
    </label>
  );
}

function SlideCard({
  form,
  index,
  expanded,
  isNew,
  saving,
  onToggle,
  onChange,
  onSave,
  onRemove,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
}: {
  form: SliderFormState;
  index: number;
  expanded: boolean;
  isNew: boolean;
  saving: boolean;
  onToggle: () => void;
  onChange: (patch: Partial<SliderFormState>) => void;
  onSave: () => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  isFirst: boolean;
  isLast: boolean;
}) {
  const imageInputRef = useRef<HTMLInputElement>(null);

  const imagePreview = useMemo(() => {
    if (form.imageFile) return URL.createObjectURL(form.imageFile);
    return form.imageUrl;
  }, [form.imageFile, form.imageUrl]);

  useEffect(() => {
    if (!form.imageFile || !imagePreview?.startsWith('blob:')) return;
    return () => URL.revokeObjectURL(imagePreview);
  }, [form.imageFile, imagePreview]);

  return (
    <article className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      <div className="flex items-center gap-3 p-4">
        <div className="h-14 w-20 shrink-0 overflow-hidden rounded-lg bg-slate-100">
          {imagePreview ? (
            <img src={imagePreview} alt={form.title} className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-slate-400">
              <ImageIcon className="h-5 w-5" />
            </div>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
            Slide {index + 1}
            {isNew && (
              <span className="ml-2 rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] text-indigo-700">
                New
              </span>
            )}
          </p>
          <p className="truncate text-sm font-semibold text-slate-900">
            {form.title || 'Untitled slide'}
          </p>
          {form.subtitle && <p className="truncate text-xs text-slate-500">{form.subtitle}</p>}
        </div>

        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={onMoveUp}
            disabled={isFirst || saving}
            className="rounded-lg p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 disabled:opacity-30"
            aria-label="Move up"
          >
            <ChevronUp className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={onMoveDown}
            disabled={isLast || saving}
            className="rounded-lg p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 disabled:opacity-30"
            aria-label="Move down"
          >
            <ChevronDown className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={onRemove}
            disabled={saving}
            className="rounded-lg p-2 text-red-400 transition hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
            aria-label="Remove slide"
          >
            <Trash2 className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={onToggle}
            className="rounded-lg p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-800"
            aria-label={expanded ? 'Collapse' : 'Expand'}
          >
            <ChevronDown className={`h-4 w-4 transition ${expanded ? 'rotate-180' : ''}`} />
          </button>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-slate-100 p-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Title" value={form.title} onChange={(v) => onChange({ title: v })} />
            <Field
              label="Subtitle"
              value={form.subtitle}
              onChange={(v) => onChange({ subtitle: v })}
            />
            <div className="sm:col-span-2">
              <Field
                label="Link URL"
                value={form.link}
                onChange={(v) => onChange({ link: v })}
                placeholder="https://..."
              />
            </div>
            <Field
              label="Sort order"
              type="number"
              value={String(form.sort_order)}
              onChange={(v) => onChange({ sort_order: Math.max(0, Number(v) || 0) })}
            />
            <label className="flex items-center gap-2 text-sm text-slate-700 self-end pb-2">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => onChange({ is_active: e.target.checked })}
                className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
              />
              Active on storefront
            </label>

            <div className="sm:col-span-2">
              <span className="mb-1.5 block text-sm font-medium text-slate-700">
                Banner image {isNew && <span className="text-red-500">*</span>}
              </span>
              <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                  <div className="h-24 w-36 shrink-0 overflow-hidden rounded-lg border border-slate-200 bg-white">
                    {imagePreview ? (
                      <img src={imagePreview} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-slate-400">
                        <ImageIcon className="h-6 w-6" />
                      </div>
                    )}
                  </div>
                  <div>
                    <button
                      type="button"
                      onClick={() => imageInputRef.current?.click()}
                      className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-indigo-200 hover:text-indigo-700"
                    >
                      <Upload className="h-4 w-4" />
                      {form.imageFile || form.imageUrl ? 'Replace image' : 'Choose image'}
                    </button>
                    <p className="mt-1.5 text-xs text-slate-500">JPEG, PNG, WebP or GIF · Max 4 MB</p>
                    {form.imageFile && (
                      <p className="mt-1 truncate text-xs text-indigo-600">{form.imageFile.name}</p>
                    )}
                  </div>
                </div>
                <input
                  ref={imageInputRef}
                  type="file"
                  accept={PRODUCT_IMAGE_ACCEPT}
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const error = validateProductImageFile(file);
                    if (error) {
                      toast.error(error);
                      e.target.value = '';
                      return;
                    }
                    if (file.size > PRODUCT_IMAGE_MAX_BYTES) {
                      toast.error('Image must be 4 MB or smaller');
                      e.target.value = '';
                      return;
                    }
                    onChange({ imageFile: file });
                    e.target.value = '';
                  }}
                />
              </div>
            </div>
          </div>

          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={onSave}
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {saving ? 'Saving…' : isNew ? 'Create slide' : 'Save slide'}
            </button>
          </div>
        </div>
      )}
    </article>
  );
}

type SlideEntry =
  | { kind: 'existing'; slider: Slider; form: SliderFormState }
  | { kind: 'new'; tempId: string; form: SliderFormState };

const ManageSlider = () => {
  const [entries, setEntries] = useState<SlideEntry[]>([]);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState<string | null>(null);

  const loadSliders = async () => {
    const data = await getSlides();
    setEntries(
      data.map((slider) => ({
        kind: 'existing' as const,
        slider,
        form: sliderToForm(slider),
      })),
    );
  };

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        await loadSliders();
      } catch {
        if (!cancelled) toast.error('Failed to load slides');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const entryKey = (entry: SlideEntry) =>
    entry.kind === 'new' ? entry.tempId : String(entry.slider.id);

  const addSlide = () => {
    const tempId = `new-${Date.now()}`;
    setEntries((prev) => [
      ...prev,
      { kind: 'new', tempId, form: emptySliderForm(prev.length) },
    ]);
    setExpandedKey(tempId);
  };

  const updateEntryForm = (index: number, patch: Partial<SliderFormState>) => {
    setEntries((prev) =>
      prev.map((entry, i) =>
        i === index ? { ...entry, form: { ...entry.form, ...patch } } : entry,
      ),
    );
  };

  const handleSave = async (index: number) => {
    const entry = entries[index];
    const key = entryKey(entry);
    const { form } = entry;

    if (entry.kind === 'new' && !form.imageFile) {
      toast.error('Banner image is required');
      return;
    }

    setSavingKey(key);
    try {
      if (entry.kind === 'new') {
        await createSlider({
          title: form.title.trim() || undefined,
          subtitle: form.subtitle.trim() || undefined,
          link: form.link.trim() || undefined,
          sort_order: form.sort_order,
          is_active: form.is_active,
          image: form.imageFile!,
        });
        toast.success('Slide created');
      } else {
        await updateSlider(entry.slider.id, {
          title: form.title.trim(),
          subtitle: form.subtitle.trim(),
          link: form.link.trim(),
          sort_order: form.sort_order,
          is_active: form.is_active,
          image: form.imageFile,
        });
        toast.success('Slide saved');
      }
      await loadSliders();
      setExpandedKey(null);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to save slide';
      toast.error(message);
    } finally {
      setSavingKey(null);
    }
  };

  const handleRemove = async (index: number) => {
    const entry = entries[index];
    if (entry.kind === 'new') {
      setEntries((prev) => prev.filter((_, i) => i !== index));
      return;
    }

    if (!window.confirm('Delete this slide? This cannot be undone.')) return;

    const key = entryKey(entry);
    setSavingKey(key);
    try {
      await deleteSlider(entry.slider.id);
      toast.success('Slide deleted');
      await loadSliders();
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to delete slide';
      toast.error(message);
    } finally {
      setSavingKey(null);
    }
  };

  const moveSlide = async (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= entries.length) return;

    const a = entries[index];
    const b = entries[target];
    if (a.kind === 'new' || b.kind === 'new') {
      toast.error('Save new slides before reordering');
      return;
    }

    const key = entryKey(a);
    setSavingKey(key);
    try {
      const aOrder = a.slider.sort_order;
      const bOrder = b.slider.sort_order;
      await Promise.all([
        updateSlider(a.slider.id, { sort_order: bOrder }),
        updateSlider(b.slider.id, { sort_order: aOrder }),
      ]);
      await loadSliders();
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to reorder slides';
      toast.error(message);
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col w-full">
      <div className="flex flex-1 w-full">
        <Sidebar />
        <main className="flex-1 p-4 md:p-8 w-full max-h-screen overflow-y-auto">
          <div className="mb-6 md:mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-50 text-xs font-medium text-indigo-700 mb-3">
                <SlidersHorizontal className="h-3.5 w-3.5" />
                Homepage banner
              </div>
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">
                Manage Slider
              </h1>
              <p className="mt-1.5 text-sm md:text-base text-slate-500">
                Add, reorder, and edit hero banner slides for your storefront.
              </p>
            </div>
            <button
              type="button"
              onClick={addSlide}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-indigo-200 hover:text-indigo-700"
            >
              <Plus className="h-4 w-4" />
              Add slide
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-24">
              <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
            </div>
          ) : entries.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center">
              <SlidersHorizontal className="mx-auto h-10 w-10 text-slate-300" />
              <p className="mt-3 text-sm font-medium text-slate-700">No slides yet</p>
              <p className="mt-1 text-sm text-slate-500">Add your first banner slide to get started.</p>
              <button
                type="button"
                onClick={addSlide}
                className="mt-4 inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700"
              >
                <Plus className="h-4 w-4" />
                Add slide
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {entries.map((entry, index) => {
                const key = entryKey(entry);
                return (
                  <SlideCard
                    key={key}
                    form={entry.form}
                    index={index}
                    expanded={expandedKey === key}
                    isNew={entry.kind === 'new'}
                    saving={savingKey === key}
                    onToggle={() => setExpandedKey((id) => (id === key ? null : key))}
                    onChange={(patch) => updateEntryForm(index, patch)}
                    onSave={() => void handleSave(index)}
                    onRemove={() => void handleRemove(index)}
                    onMoveUp={() => void moveSlide(index, -1)}
                    onMoveDown={() => void moveSlide(index, 1)}
                    isFirst={index === 0}
                    isLast={index === entries.length - 1}
                  />
                );
              })}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default ManageSlider;
